import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, RefreshCcw, FileText, Loader2, Sparkles, Send, Eye, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerTrigger } from '@/components/ui/drawer';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import StatusBadge from '@/components/StatusBadge';

const LANGUAGES = ['en_US', 'en', 'en_GB', 'hi', 'mr', 'gu', 'ta', 'te', 'kn'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];

function AIAssistDrawer({ open, onOpenChange, onApply }) {
  const [desc, setDesc] = useState('');
  const [headerType, setHeaderType] = useState('TEXT');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!desc.trim()) {
      toast.error('Please describe your campaign');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/templates/ai-generate', { description: desc, header_type: headerType });
      setResult(r.data);
      toast.success('Template generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!result) return;
    onApply({ ...result, header_type: headerType });
    setResult(null);
    setDesc('');
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="glass-strong border-white/40 max-h-[92vh]">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#F25C2A]" /> AI Assist — Generate Template</DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-2 space-y-3 overflow-y-auto">
          <div className="space-y-1.5">
            <Label>Describe the campaign</Label>
            <Textarea
              rows={4}
              placeholder="e.g., Diwali special: 10% off on Goa 4-night package. Promo code GOA10. Personalize with customer name."
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              data-testid="ai-assist-description-input"
              className="bg-white/70 border-white/60 rounded-xl"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Header media type</Label>
            <Select value={headerType} onValueChange={setHeaderType}>
              <SelectTrigger data-testid="ai-assist-header-select" className="bg-white/70 border-white/60 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HEADER_TYPES.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generate} disabled={busy} className="w-full gradient-orange text-white rounded-xl" data-testid="ai-assist-generate-button">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate with Gemini
          </Button>
          {result && (
            <div className="glass p-3 rounded-xl space-y-2">
              <div><span className="text-xs uppercase text-muted-foreground">Suggested name</span><div className="font-mono text-sm">{result.template_name}</div></div>
              {result.header_text && (<div><span className="text-xs uppercase text-muted-foreground">Header</span><div className="text-sm">{result.header_text}</div></div>)}
              <div><span className="text-xs uppercase text-muted-foreground">Body</span><div className="text-sm whitespace-pre-wrap">{result.body_text}</div></div>
              {result.footer_text && (<div><span className="text-xs uppercase text-muted-foreground">Footer</span><div className="text-sm">{result.footer_text}</div></div>)}
              {result.sample_variables?.length > 0 && (<div><span className="text-xs uppercase text-muted-foreground">Sample variables</span><div className="text-sm">{result.sample_variables.join(', ')}</div></div>)}
            </div>
          )}
        </div>
        <DrawerFooter className="gap-2">
          <Button onClick={apply} disabled={!result} className="gradient-orange text-white rounded-xl" data-testid="ai-assist-apply-button">Use this template</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function TemplateDialog({ open, onOpenChange, template, onSaved }) {
  const [form, setForm] = useState({
    name: '', category: 'MARKETING', language: 'en_US', header_type: 'NONE', header_text: '',
    body_text: '', footer_text: '', sample_variables: [],
  });
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        category: template.category || 'MARKETING',
        language: template.language || 'en_US',
        header_type: template.header_type || 'NONE',
        header_text: template.header_text || '',
        body_text: template.body_text || '',
        footer_text: template.footer_text || '',
        sample_variables: template.sample_variables || [],
      });
    } else {
      setForm({ name: '', category: 'MARKETING', language: 'en_US', header_type: 'NONE', header_text: '', body_text: '', footer_text: '', sample_variables: [] });
    }
  }, [template, open]);

  const onAIApply = (g) => {
    setForm({
      name: g.template_name,
      category: 'MARKETING',
      language: form.language || 'en_US',
      header_type: g.header_type || 'TEXT',
      header_text: g.header_text || '',
      body_text: g.body_text || '',
      footer_text: g.footer_text || '',
      sample_variables: g.sample_variables || [],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, buttons: [] };
      if (template?.id) {
        await api.put(`/templates/${template.id}`, payload);
      } else {
        await api.post('/templates/', payload);
      }
      toast.success('Template saved as DRAFT');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="glass-strong border-white/40 sm:max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{template ? 'Edit Template' : 'New Template'}</span>
              <Button variant="outline" size="sm" className="rounded-xl bg-white/60 border-white/60" onClick={() => setAiOpen(true)} data-testid="templates-ai-assist-open-button">
                <Sparkles className="h-4 w-4 mr-1.5 text-[#F25C2A]" /> AI Assist
              </Button>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5 md:col-span-1">
                <Label>Name (snake_case)</Label>
                <Input data-testid="template-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })} placeholder="goa_diwali_offer" className="bg-white/70 border-white/60 rounded-xl font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="template-category-select" className="bg-white/70 border-white/60 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="template-language-select" className="bg-white/70 border-white/60 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Header Type</Label>
                <Select value={form.header_type} onValueChange={(v) => setForm({ ...form, header_type: v })}>
                  <SelectTrigger data-testid="template-header-type-select" className="bg-white/70 border-white/60 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{HEADER_TYPES.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground">For IMAGE/VIDEO/DOCUMENT, attach media when creating a campaign.</div>
              </div>
              {form.header_type === 'TEXT' && (
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Header Text (max 60 chars)</Label>
                  <Input data-testid="template-header-text-input" maxLength={60} value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Body Text (max 1024 chars). Use <code>{`{{1}}`}</code>, <code>{`{{2}}`}</code> for variables.</Label>
              <Textarea data-testid="template-body-input" required rows={5} maxLength={1024} value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
              <div className="text-[11px] text-muted-foreground">Detected variables: {(form.body_text.match(/\{\{(\d+)\}\}/g) || []).length}</div>
            </div>

            <div className="space-y-1.5">
              <Label>Footer Text (optional, max 60 chars)</Label>
              <Input data-testid="template-footer-input" maxLength={60} value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
            </div>

            <div className="space-y-1.5">
              <Label>Sample Variable Values (comma-separated; for Meta approval preview)</Label>
              <Input data-testid="template-samples-input" value={(form.sample_variables || []).join(', ')} onChange={(e) => setForm({ ...form, sample_variables: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Aisha, GOA10" className="bg-white/70 border-white/60 rounded-xl" />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60">Cancel</Button>
              <Button type="submit" disabled={saving} className="gradient-orange text-white rounded-xl" data-testid="template-save-button">
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save Draft
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AIAssistDrawer open={aiOpen} onOpenChange={setAiOpen} onApply={onAIApply} />
    </>
  );
}

function PreviewDialog({ open, onOpenChange, template }) {
  if (!template) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-md rounded-2xl">
        <DialogHeader><DialogTitle>Preview — {template.name}</DialogTitle></DialogHeader>
        <div className="rounded-2xl bg-[#FFF7ED] border border-[#FDBA74]/40 p-4 shadow-inner">
          {template.header_type === 'TEXT' && template.header_text && (
            <div className="font-bold text-base mb-1">{template.header_text}</div>
          )}
          {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.header_type) && (
            <div className="mb-2 rounded-xl bg-white/70 border border-white/60 p-3 text-xs text-muted-foreground">[{template.header_type} attached at send time]</div>
          )}
          <div className="text-sm whitespace-pre-wrap">{template.body_text}</div>
          {template.footer_text && <div className="text-xs text-muted-foreground mt-2">{template.footer_text}</div>}
        </div>
        <div className="text-xs text-muted-foreground">Status: <StatusBadge status={template.status} kind="template" /> · {template.language} · {template.category}</div>
        {template.rejection_reason && template.rejection_reason !== 'NONE' && (
          <div className="mt-2 text-sm text-[#9F1239]">Rejection: {template.rejection_reason}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/templates/');
      setTemplates(r.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadSample = async (t, file) => {
    if (!file) return;
    setUploadingId(t.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/templates/${t.id}/sample-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Sample uploaded. You can now submit this template to Meta.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sample upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const submitToMeta = async (t) => {
    if (!window.confirm(`Submit template "${t.name}" to Meta for approval?`)) return;
    setBusyId(t.id);
    try {
      await api.post(`/templates/${t.id}/submit`);
      toast.success('Submitted. Meta will review within 24 hours.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setBusyId(null);
    }
  };

  const refresh = async (t) => {
    setBusyId(t.id);
    try {
      const r = await api.post(`/templates/${t.id}/refresh`);
      toast.success(`Status: ${r.data.status}`);
      load();
    } catch (err) {
      toast.error('Refresh failed');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Will also remove from Meta if submitted.`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const syncFromMeta = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/templates/sync-from-meta');
      toast.success(`Synced ${r.data.synced} from Meta`);
      load();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="templates-heading">Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Create approval-friendly WhatsApp marketing templates with AI assistance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={syncFromMeta} disabled={syncing} className="rounded-xl bg-white/60 border-white/60" data-testid="templates-sync-button">
            {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1.5" />} Sync from Meta
          </Button>
          <Button onClick={() => { setEditTarget(null); setOpen(true); }} className="gradient-orange text-white rounded-xl" data-testid="templates-create-button">
            <Plus className="h-4 w-4 mr-1.5" /> New Template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="glass-card p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : !templates.length ? (
        <div className="glass-card p-10 text-center">
          <div className="inline-flex h-12 w-12 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-3"><FileText className="h-6 w-6" /></div>
          <div className="text-sm text-muted-foreground mb-3">No templates yet. Use AI Assist to generate one in seconds.</div>
          <Button onClick={() => setOpen(true)} className="gradient-orange text-white rounded-xl">Create Template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="glass-card p-4 sm:p-5 flex flex-col" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm truncate" data-testid={`template-name-${t.id}`}>{t.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t.category} · {t.language} · {t.header_type} · {t.variables_count} vars</div>
                </div>
                <StatusBadge status={t.status} kind="template" />
              </div>
              <div className="mt-3 text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">{t.body_text}</div>
              {t.rejection_reason && t.rejection_reason !== 'NONE' && <div className="mt-2 text-xs text-[#9F1239] line-clamp-2">Reason: {t.rejection_reason}</div>}
              <div className="mt-auto pt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-xl bg-white/60 border-white/60" onClick={() => setPreviewTarget(t)} data-testid={`template-preview-${t.id}`}><Eye className="h-4 w-4 mr-1.5" />Preview</Button>
                {(t.status === 'DRAFT' || t.status === 'REJECTED') && (
                  <Button size="sm" variant="outline" className="rounded-xl bg-white/60 border-white/60" onClick={() => { setEditTarget(t); setOpen(true); }} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
                )}
                {(t.status === 'DRAFT' || t.status === 'REJECTED') && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(t.header_type) && (
                  <label className={`inline-flex items-center gap-1.5 cursor-pointer rounded-xl border bg-white/60 border-white/60 px-3 h-9 text-sm font-medium hover:bg-white/80 ${uploadingId === t.id ? 'opacity-60 pointer-events-none' : ''} ${t.header_media_handle ? 'text-[#0F6B45] border-[#86EFAC] bg-[#EAF8F1]' : ''}`} data-testid={`template-upload-sample-${t.id}`}>
                    {uploadingId === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <span>{t.header_media_handle ? `Sample uploaded · Replace` : `Upload sample ${t.header_type.toLowerCase()}`}</span>
                    <input type="file" className="hidden" accept={t.header_type === 'IMAGE' ? 'image/*' : t.header_type === 'VIDEO' ? 'video/*' : '.pdf,application/pdf'} onChange={(e) => uploadSample(t, e.target.files?.[0])} />
                  </label>
                )}
                {(t.status === 'DRAFT' || t.status === 'REJECTED') && (
                  <Button size="sm" className="rounded-xl gradient-orange text-white" onClick={() => submitToMeta(t)} disabled={busyId === t.id} data-testid={`template-submit-${t.id}`}>
                    {busyId === t.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Submit
                  </Button>
                )}
                {(t.meta_template_id || ['PENDING', 'APPROVED', 'REJECTED'].includes(t.status)) && (
                  <Button size="sm" variant="outline" className="rounded-xl bg-white/60 border-white/60" onClick={() => refresh(t)} disabled={busyId === t.id} data-testid={`template-refresh-${t.id}`}>
                    {busyId === t.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1.5" />} Refresh
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => onDelete(t)} aria-label="Delete" data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4 text-[#9F1239]" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateDialog open={open} onOpenChange={setOpen} template={editTarget} onSaved={load} />
      <PreviewDialog open={!!previewTarget} onOpenChange={(v) => !v && setPreviewTarget(null)} template={previewTarget} />
    </div>
  );
}
